/*@jsxRuntime automatic @jsxImportSource react*/
import {Fragment as _Fragment, jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime";
import {useMDXComponents as _provideComponents} from "@mdx-js/react";
import {Docs} from 'components/layout';
import {getIntegrations} from 'api/get-integrations';
import {getExtensions} from 'api/get-extensions';
import {Warning, Tip, Info, WhitelistDetails} from 'ui';
export const title = "Salesforce Marketing Cloud integration with AI Decisioning", description = "Learn how to connect Salesforce Marketing Cloud (SFMC) to AI Decisioning in Hightouch to send personalized, real-time messages across email, SMS, push, and in-app channels.", lastUpdatedDate = new Date(1790121600000);
export async function getStaticProps() {
  const integrations = await getIntegrations();
  const extensions = await getExtensions();
  return {
    props: {
      ...integrations,
      extensions,
      meta: {
        title: "Salesforce Marketing Cloud integration with AI Decisioning",
        description: "Learn how to connect Salesforce Marketing Cloud (SFMC) to AI Decisioning in Hightouch to send personalized, real-time messages across email, SMS, push, and in-app channels.",
        lastUpdatedDate: "Wed Sep 23 2026 00:00:00 GMT+0000 (Coordinated Universal Time)",
        slug: null,
        type: null,
        icon: null,
        toc: [{
          "value": "Overview",
          "depth": 0,
          "id": "overview"
        }, {
          "value": "1. Set up SFMC as a destination",
          "depth": 0,
          "id": "1-set-up-sfmc-as-a-destination"
        }, {
          "value": "Why these permissions matter",
          "depth": 1,
          "id": "why-these-permissions-matter"
        }, {
          "value": "Connect SFMC and grant permissions",
          "depth": 1,
          "id": "connect-sfmc-and-grant-permissions"
        }, {
          "value": "2. Add channels in AI Decisioning",
          "depth": 0,
          "id": "2-add-channels-in-ai-decisioning"
        }, {
          "value": "Set up a channel",
          "depth": 1,
          "id": "set-up-a-channel"
        }, {
          "value": "Sync type comparison",
          "depth": 1,
          "id": "sync-type-comparison"
        }, {
          "value": "3. Add messages",
          "depth": 0,
          "id": "3-add-messages"
        }, {
          "value": "Add a message from SFMC",
          "depth": 1,
          "id": "add-a-message-from-sfmc"
        }, {
          "value": "Test a message",
          "depth": 1,
          "id": "test-a-message"
        }, {
          "value": "4. Configure SFMC Triggered Sends and Journeys for AID",
          "depth": 0,
          "id": "4-configure-sfmc-triggered-sends-and-journeys-for-aid"
        }, {
          "value": "Send emails via Triggered Send",
          "depth": 1,
          "id": "send-emails-via-triggered-send"
        }, {
          "value": "Send emails via Journey",
          "depth": 1,
          "id": "send-emails-via-journey"
        }, {
          "value": "Send emails via Data Extension",
          "depth": 1,
          "id": "send-emails-via-data-extension"
        }, {
          "value": "Send SMS text messages via Journey",
          "depth": 1,
          "id": "send-sms-text-messages-via-journey"
        }, {
          "value": "Send SMS text messages via Data Extension",
          "depth": 1,
          "id": "send-sms-text-messages-via-data-extension"
        }, {
          "value": "Send push (outbound) notifications via Triggered Send",
          "depth": 1,
          "id": "send-push-outbound-notifications-via-triggered-send"
        }, {
          "value": "Send push (app inbox) notifications via Triggered Send",
          "depth": 1,
          "id": "send-push-app-inbox-notifications-via-triggered-send"
        }, {
          "value": "Send push notifications via Journey",
          "depth": 1,
          "id": "send-push-notifications-via-journey"
        }, {
          "value": "Send push notifications via Data Extension",
          "depth": 1,
          "id": "send-push-notifications-via-data-extension"
        }, {
          "value": "Send in-app notifications via Journey",
          "depth": 1,
          "id": "send-in-app-notifications-via-journey"
        }, {
          "value": "Send in-app notifications via Data Extension",
          "depth": 1,
          "id": "send-in-app-notifications-via-data-extension"
        }, {
          "value": "Use variables in SFMC",
          "depth": 0,
          "id": "use-variables-in-sfmc"
        }, {
          "value": "Access variables",
          "depth": 1,
          "id": "access-variables"
        }, {
          "value": "Important notes",
          "depth": 1,
          "id": "important-notes"
        }, {
          "value": "Troubleshooting",
          "depth": 0,
          "id": "troubleshooting"
        }, {
          "value": "Next steps",
          "depth": 0,
          "id": "next-steps"
        }]
      }
    },
    revalidate: 60
  };
}
const MDXLayout = ({children, ...props}) => _jsx(Docs, Object.assign({}, props, {
  children: children
}));
function MDXContent(props = {}) {
  return MDXLayout ? _jsx(MDXLayout, Object.assign({}, props, {
    children: _jsx(_createMdxContent, {})
  })) : _createMdxContent();
  function _createMdxContent() {
    const _components = Object.assign({
      table: "table",
      thead: "thead",
      tr: "tr",
      th: "th",
      tbody: "tbody",
      td: "td",
      strong: "strong",
      a: "a",
      p: "p",
      em: "em",
      hr: "hr",
      h2: "h2",
      ul: "ul",
      li: "li",
      h3: "h3",
      ol: "ol",
      img: "img",
      code: "code",
      h4: "h4"
    }, _provideComponents(), props.components), {PylonLink} = _components;
    if (!PylonLink) _missingMdxReference("PylonLink", true);
    return _jsxs(_Fragment, {
      children: [_jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {}), _jsx(_components.th, {})]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Audience"
              })
            }), _jsx(_components.td, {
              children: "Technical marketers, marketing and lifecycle teams"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Prerequisites"
              })
            }), _jsx(_components.td, {
              children: _jsx(_components.a, {
                href: "/ai-decisioning/prepare-data",
                children: "Prepare data for AID"
              })
            })]
          })]
        })]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.em, {
          children: "Connect Salesforce Marketing Cloud to AI Decisioning so agents can deliver personalized emails, SMS, push notifications, and in-app messages through your existing SFMC Triggered Sends and Journeys."
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "overview",
        children: "Overview"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch's AI Decisioning (AID) helps you deliver real-time, personalized messages based on live customer context and predictions. By connecting AID with Salesforce Marketing Cloud (SFMC), you can:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Automatically trigger messages from AID Agents via SFMC Triggered Sends or Journeys"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Personalize SFMC templates using dynamic variables from Hightouch—no custom code required"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Centralize targeting and decision logic in Hightouch, while using SFMC to handle message delivery"
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "1-set-up-sfmc-as-a-destination",
        children: "1. Set up SFMC as a destination"
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Already configured SFMC? Even if you've previously set SFMC up as a\ndestination, you'll still need to apply the specific permissions and scopes\noutlined below."
        })
      }), "\n", _jsx(_components.h3, {
        id: "why-these-permissions-matter",
        children: "Why these permissions matter"
      }), "\n", _jsx(_components.p, {
        children: "To send messages through SFMC using AID, Hightouch needs access to certain channels, content, and user data. These permissions allow Hightouch to:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Trigger messages via Triggered Send and Journey Builder"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Read and update subscriber data"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Access and personalize email, SMS, push, and in-app templates"
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsx(_components.p, {
            children: "Read Content Builder assets to list and preview your email templates"
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "connect-sfmc-and-grant-permissions",
        children: "Connect SFMC and grant permissions"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In SFMC:"
        })
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: [_jsx(_components.a, {
              href: "/destinations/sfmc#create-installed-package-in-sfmc",
              children: "Create an installed package in SFMC"
            }), " until you reach ", _jsx(_components.strong, {
              children: "Set Server-to-Server Properties"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "Channels"
            }), ", enable the permissions for each message type you want Hightouch to use (read, write, send, publish, or post)."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/installed-package-permissions.png",
          alt: "Add component"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "Assets"
            }), ", select ", _jsx(_components.strong, {
              children: "Documents and Images: Read"
            }), "."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Select ", _jsx(_components.strong, {
              children: "Save"
            }), "."]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In Hightouch:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Continue setting up the SFMC destination until you reach the ", _jsx(_components.a, {
              href: "/destinations/sfmc#custom-scopes",
              children: "Custom Scopes"
            }), " step."]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Under ", _jsx(_components.strong, {
              children: "Advanced permissions > Custom Scopes"
            }), ", paste in the following:"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.code, {
          children: "email_read email_write email_send documents_and_images_read accounts_read data_extensions_read data_extensions_write journeys_read journeys_write journeys_aspr journeys_execute list_and_subscribers_write list_and_subscribers_read push_read push_write push_send"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "7",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Test Connection"
          }), " to verify that setup is complete."]
        }), "\n"]
      }), "\n", _jsx(_components.h2, {
        id: "2-add-channels-in-ai-decisioning",
        children: "2. Add channels in AI Decisioning"
      }), "\n", _jsxs(_components.p, {
        children: ["Once SFMC is set up as a destination, you can add ", _jsx(_components.strong, {
          children: "channels"
        }), ". Channels define how Hightouch sends messages to SFMC—via Triggered Sends or Journeys."]
      }), "\n", _jsx(_components.h3, {
        id: "set-up-a-channel",
        children: "Set up a channel"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Configuration > Channels"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add channel"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-add-channel.png",
          alt: "Add channel button"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Choose a channel type: ", _jsx(_components.strong, {
            children: "Email"
          }), ", ", _jsx(_components.strong, {
            children: "SMS"
          }), ", ", _jsx(_components.strong, {
            children: "Push"
          }), ", or ", _jsx(_components.strong, {
            children: "In-App"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Salesforce Marketing Cloud"
          }), " as the destination."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select a ", _jsx(_components.a, {
            href: "#sync-type-comparison",
            children: "sync type"
          }), ":", "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Triggered send"
              }), " – sends a 1:1 message in real time"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Journey entry"
              }), " – sends as part of a journey workflow"]
            }), "\n", _jsxs(_components.li, {
              children: [_jsx(_components.strong, {
                children: "Data extension (advanced)"
              }), " – batch sends managed within SFMC"]
            }), "\n"]
          }), "\n"]
        }), "\n", _jsxs(_components.li, {
          children: ["For ", _jsx(_components.strong, {
            children: "Triggered send"
          }), " channels, choose an ", _jsx(_components.strong, {
            children: "identifier"
          }), " that matches the Subscriber Key field in SFMC (for example, email or user ID). Hightouch sets the identifier automatically for Journey and Data extension channels."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add channel"
          }), ", then ", _jsx(_components.strong, {
            children: "Save changes"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-add-channel-sfmc.png",
          alt: "New channel modal"
        })
      }), "\n", _jsx(_components.h3, {
        id: "sync-type-comparison",
        children: "Sync type comparison"
      }), "\n", _jsxs(_components.p, {
        children: ["The ", _jsx(_components.strong, {
          children: "Sync Type"
        }), " determines how Hightouch sends messages through SFMC:"]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Channel"
            }), _jsx(_components.th, {
              align: "center",
              children: "Triggered send"
            }), _jsx(_components.th, {
              align: "center",
              children: "Journey"
            }), _jsx(_components.th, {
              align: "center",
              children: "Data extension"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Email"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "SMS"
            }), _jsx(_components.td, {
              align: "center",
              children: "❌"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Push"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "In-App"
            }), _jsx(_components.td, {
              align: "center",
              children: "❌"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            }), _jsx(_components.td, {
              align: "center",
              children: "✅"
            })]
          })]
        })]
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Sync type"
            }), _jsx(_components.th, {
              children: "Best for"
            }), _jsx(_components.th, {
              children: "Setup notes"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Triggered send"
              })
            }), _jsx(_components.td, {
              children: "Real-time, event-based sends tied to a single user action"
            }), _jsxs(_components.td, {
              children: ["For ", _jsx(_components.strong, {
                children: "email"
              }), " Triggered Sends, select a ", _jsx(_components.strong, {
                children: "send classification"
              }), " from your SFMC account — for example, a Commercial classification for marketing (users can opt out) or a Transactional one for system notifications (users can't). SFMC maps these to its Marketing and Operational types."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Journey entry"
              })
            }), _jsx(_components.td, {
              children: "Automated, multi-step campaigns (for example, onboarding, retargeting)"
            }), _jsxs(_components.td, {
              children: ["Select the ", _jsx(_components.strong, {
                children: "Synced user columns"
              }), " (for example, email, name, product interest) you want to sync. These fields are available inside the journey's Data Extension for personalization or decision splits."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: _jsx(_components.strong, {
                children: "Data extension (advanced)"
              })
            }), _jsx(_components.td, {
              children: "Batch sends, scheduled campaigns, and advanced setups where you need full control over delivery logic"
            }), _jsxs(_components.td, {
              children: ["A ", _jsx(_components.a, {
                href: "https://help.salesforce.com/s/articleView?id=mktg.mc_es_data_extension_data_relationships_classic.htm&type=5",
                children: "Data Extension"
              }), " is a table in SFMC that stores subscriber or message data. When you choose this sync type, Hightouch writes data directly to a Data Extension, and you manage the send logic (timing, audience filters, scheduling) within SFMC. This gives you the most flexibility but requires more setup on the SFMC side. Choose ", _jsx(_components.strong, {
                children: "Insert"
              }), " to append new rows, or ", _jsx(_components.strong, {
                children: "Mirror"
              }), " to keep the Data Extension in sync with your warehouse. Mirror mode may need to be enabled for your workspace — ", _jsx(PylonLink, {
                pylonText: "Hi, I don't see Mirror mode for an SFMC data extension sync.",
                linkText: "contact Hightouch"
              }), " if you don't see it."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "3-add-messages",
        children: "3. Add messages"
      }), "\n", _jsxs(_components.p, {
        children: ["After you add channels in AI Decisioning, you can add ", _jsx(_components.strong, {
          children: "messages"
        }), " to your agents. Each message connects an agent's decision to a specific template in SFMC."]
      }), "\n", _jsxs(_components.p, {
        children: ["For full details on message configuration, variants, and tags, see ", _jsx(_components.a, {
          href: "/ai-decisioning/messages",
          children: "Messages"
        }), "."]
      }), "\n", _jsx(_components.h3, {
        id: "add-a-message-from-sfmc",
        children: "Add a message from SFMC"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "AI Decisioning > Agents"
          }), " and select your agent."]
        }), "\n", _jsxs(_components.li, {
          children: ["Open the ", _jsx(_components.strong, {
            children: "Messages"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Add messages"
          }), ", then select ", _jsx(_components.strong, {
            children: "Add messages individually"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select your configured SFMC ", _jsx(_components.strong, {
            children: "channel and destination"
          }), " tile (for example, ", _jsx(_components.strong, {
            children: "Email - Salesforce Marketing Cloud"
          }), ")."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the appropriate ", _jsx(_components.strong, {
            children: "template"
          }), " or ", _jsx(_components.a, {
            href: "https://help.salesforce.com/s/articleView?id=mktg.mc_jb_create_journey_specific_entry_event.htm&type=5",
            children: "event identifier"
          }), " from SFMC."]
        }), "\n", _jsxs(_components.li, {
          children: ["Enter a ", _jsx(_components.strong, {
            children: "message name"
          }), " and select ", _jsx(_components.strong, {
            children: "Add messages"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message from SFMC"
        })
      }), "\n", _jsx(_components.p, {
        children: "You can repeat this process for any channel and sync type. Continue the setup process in the channel-specific sections below:"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-emails-via-triggered-send",
            children: "Send emails via Triggered Send"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-emails-via-journey",
            children: "Send emails via Journey"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-emails-via-data-extension",
            children: "Send emails via Data Extension"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-sms-text-messages-via-journey",
            children: "Send SMS via Journey"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-sms-text-messages-via-data-extension",
            children: "Send SMS via Data Extension"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-push-outbound-notifications-via-triggered-send",
            children: "Send push (outbound) via Triggered Send"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-push-app-inbox-notifications-via-triggered-send",
            children: "Send push (app inbox) via Triggered Send"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-push-notifications-via-journey",
            children: "Send push via Journey"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-push-notifications-via-data-extension",
            children: "Send push via Data Extension"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-in-app-notifications-via-journey",
            children: "Send in-app via Journey"
          })
        }), "\n", _jsx(_components.li, {
          children: _jsx(_components.a, {
            href: "#send-in-app-notifications-via-data-extension",
            children: "Send in-app via Data Extension"
          })
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "test-a-message",
        children: "Test a message"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open the message and go to the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Optimized content"
          }), ", confirm your variants are configured. If your message uses ", _jsx(_components.a, {
            href: "#use-variables-in-sfmc",
            children: "dynamic variables"
          }), ", set values for testing."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Preview"
          }), " panel, verify the rendered message looks correct."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Send test"
          }), " to send the message to a test user."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "4-configure-sfmc-triggered-sends-and-journeys-for-aid",
        children: "4. Configure SFMC Triggered Sends and Journeys for AID"
      }), "\n", _jsx(_components.p, {
        children: "Before adding a message in Hightouch, you need to configure the corresponding Triggered Send or Journey in SFMC."
      }), "\n", _jsx(_components.h3, {
        id: "send-emails-via-triggered-send",
        children: "Send emails via Triggered Send"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Triggered send"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-triggered-send.png",
          alt: "Email channel - Triggered Send"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC email template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Close the window that opens and return to the ", _jsx(_components.strong, {
            children: "Messages"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the link next to ", _jsx(_components.strong, {
            children: "Base content"
          }), " to go to SFMC."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-base-content-link.png",
          alt: "Base content link"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "5",
        children: ["\n", _jsxs(_components.li, {
          children: ["This will prompt you to log in to SFMC and should open the ", _jsx(_components.strong, {
            children: "Email Studio > Interactions"
          }), " page."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Triggered Sends"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-email-studio.png",
          alt: "Interactions"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "7",
        children: ["\n", _jsx(_components.li, {
          children: "Select the send name that corresponds with your newly created message."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Start/Restart"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-start.png",
          alt: "Start"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "9",
        children: ["\n", _jsxs(_components.li, {
          children: ["Return to ", _jsx(_components.strong, {
            children: "Hightouch"
          }), " and open your message."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab, ", _jsx(_components.a, {
            href: "#use-variables-in-sfmc",
            children: "set the variables"
          }), " and select ", _jsx(_components.strong, {
            children: "Send test"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.h3, {
        id: "send-emails-via-journey",
        children: "Send emails via Journey"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Journey entry"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-journey.png",
          alt: "Email channel - Journey entry"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC email template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab, ", _jsx(_components.a, {
            href: "#use-variables-in-sfmc",
            children: "set the variables"
          }), " and select ", _jsx(_components.strong, {
            children: "Send test"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.h3, {
        id: "send-emails-via-data-extension",
        children: "Send emails via Data Extension"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Data extension"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-data-extension.png",
          alt: "Email channel - Data Extension"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Choose ", _jsx(_components.strong, {
            children: "Insert"
          }), " (append new rows) or ", _jsx(_components.strong, {
            children: "Mirror"
          }), " (keep in sync with your warehouse)."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC email template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "In SFMC, configure your send logic (automation, query activity, or triggered automation) to read from the Data Extension that Hightouch populates."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "send-sms-text-messages-via-journey",
        children: "Send SMS text messages via Journey"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Journey entry"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-sms-journey.png",
          alt: "SMS channel - Journey entry"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC SMS template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab, ", _jsx(_components.a, {
            href: "#use-variables-in-sfmc",
            children: "set the variables"
          }), " and select ", _jsx(_components.strong, {
            children: "Send test"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.h3, {
        id: "send-sms-text-messages-via-data-extension",
        children: "Send SMS text messages via Data Extension"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Data extension"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-sms-data-extension.png",
          alt: "SMS channel - Data Extension"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Choose ", _jsx(_components.strong, {
            children: "Insert"
          }), " or ", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC SMS template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "In SFMC, configure your send logic to read from the Data Extension that Hightouch populates."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "send-push-outbound-notifications-via-triggered-send",
        children: "Send push (outbound) notifications via Triggered Send"
      }), "\n", _jsx(_components.h4, {
        id: "enable-the-push-channel",
        children: "Enable the push channel"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Triggered send"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-push-triggered-send.png",
          alt: "Push channel - Triggered Send"
        })
      }), "\n", _jsx(_components.h4, {
        id: "configure-your-push-template-in-sfmc",
        children: "Configure your push template in SFMC"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In SFMC:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Mobile Studio > MobilePush"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create Message"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-new-mobilepush-message.png",
          alt: "Mobile push"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Outbound"
          }), " template and select ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Name your message and select your app."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "API Triggered"
          }), " as the Send Method."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Alert"
          }), " as the Push Method and select ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-define-message-properties.png",
          alt: "API Triggered"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You can only trigger Alert Only or Inbox Only messages, not Alert + Inbox."
        })
      }), "\n", _jsxs(_components.ol, {
        start: "8",
        children: ["\n", _jsx(_components.li, {
          children: "Create the template for your push alert."
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save"
          }), " your message."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-save-push-alert.png",
          alt: "Save alert"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "10",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Send"
          }), " in the top right-hand corner."]
        }), "\n", _jsx(_components.li, {
          children: "An activation window should pop up with an API key. Copy this API key to make it easier to find the template in Hightouch in a later step."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-activate-push-alert.png",
          alt: "Activate"
        })
      }), "\n", _jsx(_components.h4, {
        id: "add-a-message-in-hightouch",
        children: "Add a message in Hightouch"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In Hightouch:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "12",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC push template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsx(_components.h4, {
        id: "preview-content",
        children: "Preview content"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch automatically creates the variables for push notification title, subtitle, and body. The values you supply here are used directly in the push notification without any need for code snippets in the template. If these are left blank, Hightouch uses the default values in the template."
      }), "\n", _jsx(Warning, {
        children: _jsxs(_components.p, {
          children: ["Hightouch uses the override capabilities in the API. If even one variable has\na value supplied, the default values in the template for ", _jsx(_components.strong, {
            children: "all"
          }), " variables get\noverridden. Make sure to supply at least the default value for all variables,\neven if you only want AID to vary one."]
        })
      }), "\n", _jsx(_components.h4, {
        id: "send-tests",
        children: "Send tests"
      }), "\n", _jsxs(_components.ol, {
        start: "13",
        children: ["\n", _jsx(_components.li, {
          children: "To test push notifications, paste the contact key you use in your SFMC contacts."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "14",
        children: ["\n", _jsx(_components.li, {
          children: "Personalization strings can be supplied directly in the values for the variables without any need for code snippets in the template."
        }), "\n"]
      }), "\n", _jsxs(_components.p, {
        children: [_jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-personalization-strings.png",
          alt: "Personalization strings"
        }), "\n", _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-personalization-strings-demo.png",
          alt: "Personalization strings demo"
        })]
      }), "\n", _jsx(_components.h3, {
        id: "send-push-app-inbox-notifications-via-triggered-send",
        children: "Send push (app inbox) notifications via Triggered Send"
      }), "\n", _jsx(_components.h4, {
        id: "enable-the-push-channel-1",
        children: "Enable the push channel"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Triggered send"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-push-triggered-send.png",
          alt: "Push channel - Triggered Send"
        })
      }), "\n", _jsx(_components.h4, {
        id: "configure-your-push-template-in-sfmc-1",
        children: "Configure your push template in SFMC"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In SFMC:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Mobile Studio > MobilePush"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create Message"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-new-mobilepush-message.png",
          alt: "Mobile push"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.strong, {
            children: "Inbox"
          }), " template and select ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n", _jsx(_components.li, {
          children: "Name your message and select your app."
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "API Triggered"
          }), " as the Send Method."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Alert"
          }), " as the Push Method and select ", _jsx(_components.strong, {
            children: "Next"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-define-message-properties.png",
          alt: "Define message"
        })
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "You can only trigger Alert Only or Inbox Only messages, not Alert + Inbox."
        })
      }), "\n", _jsxs(_components.ol, {
        start: "8",
        children: ["\n", _jsx(_components.li, {
          children: "Create the template for your push alert."
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save"
          }), " your message."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-save-push-alert.png",
          alt: "Save alert"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "10",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Send"
          }), " in the top right-hand corner."]
        }), "\n", _jsx(_components.li, {
          children: "An activation window should pop up with an API key. Copy this API key to make it easier to find the template in Hightouch in a later step."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-activate-push-alert.png",
          alt: "Activate"
        })
      }), "\n", _jsx(_components.h4, {
        id: "add-a-message-in-hightouch-1",
        children: "Add a message in Hightouch"
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In Hightouch:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "12",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC push template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsx(_components.h3, {
        id: "send-push-notifications-via-journey",
        children: "Send push notifications via Journey"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Journey entry"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-push-journey.png",
          alt: "Push channel - Journey entry"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC push template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab, ", _jsx(_components.a, {
            href: "#use-variables-in-sfmc",
            children: "set the variables"
          }), " and select ", _jsx(_components.strong, {
            children: "Send test"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.h3, {
        id: "send-push-notifications-via-data-extension",
        children: "Send push notifications via Data Extension"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Data extension"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-push-data-extension.png",
          alt: "Push channel - Data Extension"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Choose ", _jsx(_components.strong, {
            children: "Insert"
          }), " or ", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC push template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "In SFMC, configure your send logic to read from the Data Extension that Hightouch populates."
        }), "\n"]
      }), "\n", _jsx(_components.h3, {
        id: "send-in-app-notifications-via-journey",
        children: "Send in-app notifications via Journey"
      }), "\n", _jsx(_components.h4, {
        id: "enable-the-in-app-channel-and-add-a-message",
        children: "Enable the in-app channel and add a message"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Journey entry"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-app-journey.png",
          alt: "In-App channel - Journey entry"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC in-app template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsx(_components.h4, {
        id: "link-your-message-to-an-sfmc-journey",
        children: "Link your message to an SFMC Journey"
      }), "\n", _jsx(_components.p, {
        children: "In these next steps, you will link your newly created message to an associated Journey in SFMC."
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In Hightouch:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "3",
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab within your message to add the variables you would like Hightouch to control."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In SFMC:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsxs(_components.li, {
          children: ["Go to ", _jsx(_components.strong, {
            children: "Journey Builder > Journey Builder"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Create New Journey"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-create-new-journey.png",
          alt: "Create journey"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "6",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Multi-Step Journey"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "API Event"
          }), " as the entry source. Drag it onto the Journey Builder."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-api-event-entry-source.png",
          alt: "API Event"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "8",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the grey ", _jsx(_components.strong, {
            children: "API Event"
          }), " icon in the Journey Builder. This will open a list of API Events."]
        }), "\n", _jsx(_components.li, {
          children: "Select the message name you chose when adding the message in Hightouch."
        }), "\n"]
      }), "\n", _jsx(Info, {
        children: _jsx(_components.p, {
          children: "Hightouch automatically creates the required Data Extension with the event so\nyou don't have to set that up."
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-select-ht-action-name.png",
          alt: "Select"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "10",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select ", _jsx(_components.strong, {
            children: "Done"
          }), "."]
        }), "\n", _jsxs(_components.li, {
          children: ["Next, drag ", _jsx(_components.strong, {
            children: "In-App Message"
          }), " to the Journey Builder."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-drag-message-journey-builder.png",
          alt: "Drag message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "12",
        children: ["\n", _jsxs(_components.li, {
          children: ["Select the grey ", _jsx(_components.strong, {
            children: "In-App Message"
          }), " icon in the Journey Builder."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Select"
          }), " (or create) your desired in-app message template."]
        }), "\n", _jsxs(_components.li, {
          children: ["In the ", _jsx(_components.strong, {
            children: "In-App Message Activity Summary"
          }), ", edit the required settings for ", _jsx(_components.strong, {
            children: "Message Configuration"
          }), " and ", _jsx(_components.strong, {
            children: "Display Trigger"
          }), ". Optionally, configure ", _jsx(_components.strong, {
            children: "Display Options"
          }), " and ", _jsx(_components.strong, {
            children: "Advanced Options"
          }), " as desired. See ", _jsx(_components.a, {
            href: "https://help.salesforce.com/s/articleView?id=mktg.mc_jb_configure_inapp_in_journey_builder.htm&type=5",
            children: "SFMC documentation"
          }), " for more details."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-app-message-activity-summary.png",
          alt: "Activity summary"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "15",
        children: ["\n", _jsxs(_components.li, {
          children: ["\n", _jsxs(_components.p, {
            children: ["Optional: If you would like Hightouch to control aspects of the In-App Message, follow the instructions for ", _jsx(_components.a, {
              href: "#use-variables-in-sfmc",
              children: "using variables in SFMC templates"
            }), "."]
          }), "\n", _jsxs(_components.ul, {
            children: ["\n", _jsxs(_components.li, {
              children: ["Paste the variable code snippets from Hightouch into SFMC:", "\n", _jsxs(_components.ul, {
                children: ["\n", _jsxs(_components.li, {
                  children: ["Select a message in ", _jsx(_components.strong, {
                    children: "Message Definition"
                  })]
                }), "\n", _jsxs(_components.li, {
                  children: ["Select ", _jsx(_components.strong, {
                    children: "Edit Selected Message"
                  })]
                }), "\n"]
              }), "\n"]
            }), "\n"]
          }), "\n"]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-message-definition.png",
          alt: "Message definition"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "16",
        children: ["\n", _jsxs(_components.li, {
          children: ["Paste the copied code snippets from Hightouch into the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab that opens."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-paste-code-snippet.png",
          alt: "Code snippet"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "17",
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Save"
          }), " your changes."]
        }), "\n", _jsxs(_components.li, {
          children: ["Optional: To continuously test sends, click the ", _jsx(_components.strong, {
            children: "Gear icon > Journey Settings"
          }), " and select re-entry any time."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Activate"
          }), " your journey."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-activate-journey.png",
          alt: "Activate"
        })
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.strong, {
          children: "In Hightouch:"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "20",
        children: ["\n", _jsxs(_components.li, {
          children: ["Return to ", _jsx(_components.strong, {
            children: "Hightouch"
          }), " and refresh the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab within your message."]
        }), "\n", _jsx(_components.li, {
          children: "Preview your content to ensure everything is accurate."
        }), "\n", _jsxs(_components.li, {
          children: ["You can now ", _jsx(_components.strong, {
            children: "Send test"
          }), " to subscribers."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-send-test.png",
          alt: "Send test"
        })
      }), "\n", _jsx(_components.h3, {
        id: "send-in-app-notifications-via-data-extension",
        children: "Send in-app notifications via Data Extension"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#set-up-a-channel",
            children: "Add a channel"
          }), " with the sync type set to ", _jsx(_components.strong, {
            children: "Data extension"
          }), "."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-channel-app-data-extension.png",
          alt: "In-App channel - Data Extension"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "2",
        children: ["\n", _jsxs(_components.li, {
          children: ["Choose ", _jsx(_components.strong, {
            children: "Insert"
          }), " or ", _jsx(_components.strong, {
            children: "Mirror"
          }), " mode."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "#add-a-message-from-sfmc",
            children: "Add a message"
          }), " from your SFMC in-app template."]
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/add-message-sfmc.png",
          alt: "Add message"
        })
      }), "\n", _jsxs(_components.ol, {
        start: "4",
        children: ["\n", _jsx(_components.li, {
          children: "In SFMC, configure your send logic to read from the Data Extension that Hightouch populates."
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx("a", {
        id: "use-variables-in-sfmc"
      }), "\n", _jsx(_components.h2, {
        id: "use-variables-in-sfmc",
        children: "Use variables in SFMC"
      }), "\n", _jsx(_components.p, {
        children: "Hightouch variables allow you to personalize SFMC messages using live warehouse data, any variable you've defined within the message such as subject line variations, or different content blocks."
      }), "\n", _jsx(_components.h3, {
        id: "access-variables",
        children: "Access variables"
      }), "\n", _jsxs(_components.ol, {
        children: ["\n", _jsxs(_components.li, {
          children: ["Open a message in Hightouch and go to the ", _jsx(_components.strong, {
            children: "Content"
          }), " tab."]
        }), "\n", _jsxs(_components.li, {
          children: ["Under ", _jsx(_components.strong, {
            children: "Optimized content"
          }), ", expand a content type (for example, ", _jsx(_components.strong, {
            children: "subject"
          }), " or ", _jsx(_components.strong, {
            children: "body"
          }), ") and add your variants."]
        }), "\n", _jsxs(_components.li, {
          children: ["Select the ", _jsx(_components.code, {
            children: "..."
          }), " menu on a content type and select ", _jsx(_components.strong, {
            children: "Copy code snippet"
          }), " to get the variable code."]
        }), "\n", _jsx(_components.li, {
          children: "Paste the code into your SFMC template. These snippets render with real values for each user when the message is sent."
        }), "\n"]
      }), "\n", _jsx(_components.p, {
        children: _jsx(_components.img, {
          src: "/ai-decisioning/sfmc/aid-sfmc-copy-code.png",
          alt: "Copy code snippet"
        })
      }), "\n", _jsx(_components.h3, {
        id: "important-notes",
        children: "Important notes"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Triggered Sends"
          }), ": Variables are fully supported."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.strong, {
            children: "Journeys"
          }), ": Variables are available via Data Extension fields synced by Hightouch."]
        }), "\n"]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "troubleshooting",
        children: "Troubleshooting"
      }), "\n", _jsxs(_components.table, {
        children: [_jsx(_components.thead, {
          children: _jsxs(_components.tr, {
            children: [_jsx(_components.th, {
              children: "Issue"
            }), _jsx(_components.th, {
              children: "Likely cause"
            }), _jsx(_components.th, {
              children: "Resolution"
            })]
          })
        }), _jsxs(_components.tbody, {
          children: [_jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Message fails to send"
            }), _jsx(_components.td, {
              children: "API errors or misconfigured destination"
            }), _jsx(_components.td, {
              children: "Check Hightouch logs for API errors."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Test user not receiving messages"
            }), _jsx(_components.td, {
              children: "Identifier mismatch"
            }), _jsx(_components.td, {
              children: "Verify the identifier matches a valid Subscriber Key in SFMC."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Triggered Send not firing"
            }), _jsx(_components.td, {
              children: "Send not started"
            }), _jsxs(_components.td, {
              children: ["Confirm the Triggered Send is in ", _jsx(_components.strong, {
                children: "Running"
              }), " status in Email Studio."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Journey sends not triggering"
            }), _jsx(_components.td, {
              children: "Journey not activated or entry event misconfigured"
            }), _jsx(_components.td, {
              children: "Confirm the Journey is activated and the API entry event is configured."
            })]
          }), _jsxs(_components.tr, {
            children: [_jsx(_components.td, {
              children: "Variables not rendering"
            }), _jsx(_components.td, {
              children: "Incorrect syntax or missing field mapping"
            }), _jsxs(_components.td, {
              children: ["Double-check variable syntax from ", _jsx(_components.strong, {
                children: "Copy code snippet"
              }), " and field mapping."]
            })]
          }), _jsxs(_components.tr, {
            children: [_jsxs(_components.td, {
              children: [_jsx(_components.code, {
                children: "Could not get email templates"
              }), ", or a ", _jsx(_components.code, {
                children: "403"
              }), " with ", _jsx(_components.code, {
                children: "errorcode 20002"
              })]
            }), _jsxs(_components.td, {
              children: ["The installed package doesn't grant ", _jsx(_components.code, {
                children: "documents_and_images_read"
              })]
            }), _jsxs(_components.td, {
              children: ["In SFMC, add ", _jsx(_components.strong, {
                children: "Assets > Documents and Images: Read"
              }), " to the installed package."]
            })]
          })]
        })]
      }), "\n", _jsx(_components.hr, {}), "\n", _jsx(_components.h2, {
        id: "next-steps",
        children: "Next steps"
      }), "\n", _jsxs(_components.ul, {
        children: ["\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/messages",
            children: "Messages"
          }), " to create and manage the message variants each agent sends through SFMC."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/qa-guide",
            children: "QA guide"
          }), " to validate messages before you launch."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/agents",
            children: "Agents"
          }), " to configure the agents that decide which messages to send."]
        }), "\n", _jsxs(_components.li, {
          children: [_jsx(_components.a, {
            href: "/ai-decisioning/insights",
            children: "Insights"
          }), " to measure how your agents and messages perform."]
        }), "\n"]
      })]
    });
  }
}
export default MDXContent;
function _missingMdxReference(id, component) {
  throw new Error("Expected " + (component ? "component" : "object") + " `" + id + "` to be defined: you likely forgot to import, pass, or provide it.");
}
